import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Dropdown, Message } from 'semantic-ui-react'
import styled from 'styled-components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { LoaderBar } from 'components'
import { useNotification } from 'hooks/toast'
import { QuestionThreadService } from 'utility/services/question-threads'
import {
    REVIEW_FLOW_CHOICES,
    buildReviewSettingsPayload,
    isReviewFlowOn,
    normalizeReviewSettings,
    reviewSettingsLoadError,
    reviewerOptions,
    threadErrorMessage,
} from 'utility/question-threads'

const Screen = styled.div`
    max-width: 900px;
    .rf-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 12px;
        margin: 8px 0 20px;
    }
    .rf-card {
        text-align: left;
        border: 2px solid #e5e7eb;
        border-radius: 10px;
        background: #fff;
        padding: 14px;
        cursor: pointer;
    }
    .rf-card.selected {
        border-color: #1b2587;
        background: #f5f7ff;
    }
    .rf-card .title {
        font-weight: 700;
        color: #111827;
        margin-bottom: 4px;
    }
    .rf-card .text {
        font-size: 13px;
        color: #4b5563;
    }
    .rf-section {
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        background: #fff;
        padding: 16px;
        margin-bottom: 16px;
    }
    .rf-section.off {
        opacity: 0.55;
    }
    .rf-label {
        font-weight: 700;
        margin-bottom: 4px;
    }
    .rf-help {
        font-size: 12px;
        color: #6b7280;
        margin-top: 4px;
    }
    .rf-row {
        margin-top: 14px;
    }
    .rf-buttons {
        display: flex;
        gap: 8px;
    }
`

/**
 * Template setting "Review flow" (GET / POST user/Template/{templateId}/ReviewSettings, numeric template id):
 * Findings and CAPA (0, default) / Question comments (1) / Both (2), who can comment, whether the department may
 * change its answer while replying, whether report approval waits until every comment is closed.
 */
export const ReviewFlowSettings = ({ templateId, templateName, onBack }) => {
    const { successToast, errorToast } = useNotification()
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState(null)
    const [knownCommenters, setKnownCommenters] = useState([])
    const [availableReviewers, setAvailableReviewers] = useState([])
    const [canEdit, setCanEdit] = useState(true)
    const [canEditCommenters, setCanEditCommenters] = useState(false)
    const [savedCommenterIds, setSavedCommenterIds] = useState([])
    const mountedRef = useRef(true)

    useEffect(
        () => () => {
            mountedRef.current = false
        },
        [],
    )

    const applySettings = s => {
        setKnownCommenters(s.Commenters)
        // a response without the list (older backend / no right to edit) keeps what was loaded
        if (s.AvailableReviewers) setAvailableReviewers(s.AvailableReviewers)
        setCanEdit(s.CanEdit)
        setCanEditCommenters(s.CanEditCommenters)
        setSavedCommenterIds(s.Commenters.map(c => c.UserId))
        setForm({
            ReviewFlow: s.ReviewFlow,
            AnswerChangeAllowed: s.AnswerChangeAllowed,
            ApprovalWaitsForComments: s.ApprovalWaitsForComments,
            ReviewDoneRequired: s.ReviewDoneRequired,
            CommenterUserIds: s.Commenters.map(c => c.UserId),
        })
    }

    useEffect(() => {
        let alive = true
        setLoading(true)
        setLoadError('')
        setForm(null)
        QuestionThreadService.getReviewSettings(templateId)
            .then(response => {
                if (!alive) return
                if (response && response.Success === false) {
                    setLoadError(threadErrorMessage({ error: response }, 'The review flow could not be loaded.'))
                    return
                }
                applySettings(normalizeReviewSettings(response))
            })
            .catch(error => {
                if (!alive) return
                setLoadError(reviewSettingsLoadError(error))
            })
            .finally(() => alive && setLoading(false))
        return () => {
            alive = false
        }
    }, [templateId])

    // the server's AvailableReviewers + saved reviewers missing from it (by saved name, never dropped)
    const userOptions = useMemo(() => reviewerOptions(availableReviewers, knownCommenters), [availableReviewers, knownCommenters])

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

    const save = async () => {
        if (!form) return
        setSaving(true)
        try {
            // the commenter list goes only when the caller may change it and changed it (else the server keeps it)
            const body = buildReviewSettingsPayload(form, { canEditCommenters, savedCommenterIds })
            const response = await QuestionThreadService.saveReviewSettings(templateId, body)
            if (!mountedRef.current) return
            if (response && response.Success === false) {
                errorToast(threadErrorMessage({ error: response }, 'The review flow could not be saved.'))
                return
            }
            if (response && response.ReviewFlow !== undefined) applySettings(normalizeReviewSettings(response))
            successToast('Review flow saved.')
        } catch (error) {
            const message = threadErrorMessage(error, 'The review flow could not be saved.')
            if (message) errorToast(message)
        } finally {
            if (mountedRef.current) setSaving(false)
        }
    }

    const header = (
        <div style={{ marginBottom: 12 }}>
            <h2 style={{ marginBottom: 2 }}>Review flow</h2>
            {templateName && <div style={{ color: '#6b7280' }}>{templateName}</div>}
        </div>
    )

    if (loading) {
        return (
            <Screen>
                {header}
                <LoaderBar />
            </Screen>
        )
    }

    // never show an editable form when the saved setting could not be read
    if (loadError || !form) {
        return (
            <Screen>
                {header}
                <Message warning>
                    <p>{loadError || 'The review flow could not be loaded.'}</p>
                    <AppSecondaryButton type="button" onClick={onBack}>
                        Back to templates
                    </AppSecondaryButton>
                </Message>
            </Screen>
        )
    }

    const commentsOn = isReviewFlowOn(form.ReviewFlow)
    const locked = !canEdit || saving

    return (
        <Screen>
            {header}
            {!canEdit && <Message info content="You can see this setting but not change it." />}
            <div className="rf-label">How are inspections of this template reviewed?</div>
            <div className="rf-cards">
                {REVIEW_FLOW_CHOICES.map(choice => (
                    <button
                        key={choice.value}
                        type="button"
                        className={`rf-card ${form.ReviewFlow === choice.value ? 'selected' : ''}`}
                        aria-pressed={form.ReviewFlow === choice.value}
                        disabled={locked}
                        onClick={() => set('ReviewFlow', choice.value)}
                    >
                        <div className="title">{choice.title}</div>
                        <div className="text">{choice.text}</div>
                    </button>
                ))}
            </div>

            <div className={`rf-section ${commentsOn ? '' : 'off'}`}>
                <div className="rf-label">Question comments</div>
                {!commentsOn && (
                    <div className="rf-help">
                        These settings apply when the review flow is "Question comments" or "Both".
                    </div>
                )}
                <div className="rf-row">
                    <div className="rf-label">Who can comment</div>
                    <Dropdown
                        fluid
                        multiple
                        search
                        selection
                        disabled={!commentsOn || locked || !canEditCommenters}
                        placeholder="Pick the reviewers (e.g. the quality team)"
                        options={userOptions}
                        value={form.CommenterUserIds}
                        onChange={(_, d) => set('CommenterUserIds', Array.isArray(d.value) ? d.value : [])}
                    />
                    {!canEditCommenters && (
                        <div className="rf-help">You can see but not change who can comment.</div>
                    )}
                    <div className="rf-help">
                        Reviewers ask questions on any answer and close them. The department of the inspection and the
                        inspector reply. Everyone who can read the inspection sees the comments.
                    </div>
                </div>
                <div className="rf-row">
                    <Checkbox
                        toggle
                        disabled={!commentsOn || locked}
                        label="Department may change its answer while replying"
                        checked={form.AnswerChangeAllowed}
                        onChange={(_, d) => set('AnswerChangeAllowed', !!d.checked)}
                    />
                    <div className="rf-help">Every change is written into the comment ("Answer changed: Yes -> No").</div>
                </div>
                <div className="rf-row">
                    <Checkbox
                        toggle
                        disabled={!commentsOn || locked}
                        label="Report approval waits until all comments are closed"
                        checked={form.ApprovalWaitsForComments}
                        onChange={(_, d) => set('ApprovalWaitsForComments', !!d.checked)}
                    />
                </div>
                {commentsOn && (
                    <div className="rf-row">
                        <Checkbox
                            toggle
                            disabled={locked}
                            label="Approval starts only after the reviewer marks the review done"
                            checked={form.ReviewDoneRequired}
                            onChange={(_, d) => set('ReviewDoneRequired', !!d.checked)}
                        />
                        <div className="rf-help">
                            The reviewer finishes the review with "Mark review done" on the inspection.
                        </div>
                    </div>
                )}
            </div>

            <div className="rf-buttons">
                {canEdit && (
                    <AppPrimaryButton type="button" loading={saving} disabled={saving} onClick={save}>
                        Save
                    </AppPrimaryButton>
                )}
                <AppSecondaryButton type="button" disabled={saving} onClick={onBack}>
                    Back
                </AppSecondaryButton>
            </div>
        </Screen>
    )
}
